const DEV_DOMAIN = 'struxsite.com'
const DEV_PREVIEW_ROOT = `dev.${DEV_DOMAIN}`
const LOGIN_SUBDOMAIN = 'login'
export const PREVIEW_LOGIN_HOST_QUERY_KEY = 'preview-login-host'

const MOBILE_SUFFIX = '-mobile'

export function normalizeHostName(hostname: string): string {
  return (hostname || '').split(':')[0].toLowerCase()
}

/**
 * Collapse the `-mobile` twin marker on a host's first label to its
 * canonical form (`acme-mobile.struxhub.com` → `acme.struxhub.com`;
 * `pr-x-mobile.dev.struxsite.com` → `pr-x.dev.struxsite.com`). Also
 * works on a bare subdomain label (`acme-mobile` → `acme`). The single
 * source for the `-mobile` canonicalization shared by Config tenant
 * resolution, Authentication.store hostname auto-match, and the login
 * portal's return-target host matching.
 */
export function stripMobileSuffix(hostname: string): string {
  const parts = (hostname || '').split('.')
  if (parts[0].endsWith(MOBILE_SUFFIX)) {
    parts[0] = parts[0].slice(0, -MOBILE_SUFFIX.length)
  }
  return parts.join('.')
}

export function getPreviewEnvironmentLabel(hostname: string): string | null {
  const normalized = normalizeHostName(hostname)
  const suffix = `.${DEV_PREVIEW_ROOT}`

  if (!normalized.endsWith(suffix)) {
    return null
  }

  const label = normalized.slice(0, -suffix.length)

  return label || null
}

export function getPreviewLoginHost(hostname: string): string | null {
  const previewLabel = getPreviewEnvironmentLabel(hostname)

  return previewLabel
    ? `${previewLabel}.${LOGIN_SUBDOMAIN}.${DEV_DOMAIN}`
    : null
}

export function shouldUsePreviewLoginHost(search: string): boolean {
  return new URLSearchParams(search).has(PREVIEW_LOGIN_HOST_QUERY_KEY)
}

export function isPreviewAuthHost(hostname: string): boolean {
  const normalized = normalizeHostName(hostname)

  return (
    normalized.endsWith(`.${LOGIN_SUBDOMAIN}.${DEV_DOMAIN}`) &&
    normalized !== `${LOGIN_SUBDOMAIN}.${DEV_DOMAIN}`
  )
}

/**
 * Inverse of {@link getPreviewLoginHost}: the preview desktop host for a
 * preview login host (`pr-x.login.struxsite.com` → `pr-x.dev.struxsite.com`).
 * Null for the canonical dev login and every non-preview host, prod included.
 */
export function getPreviewDesktopHost(hostname: string): string | null {
  if (!isPreviewAuthHost(hostname)) {
    return null
  }

  const label = normalizeHostName(hostname).slice(
    0,
    -`.${LOGIN_SUBDOMAIN}.${DEV_DOMAIN}`.length,
  )

  return label && !label.includes('.') ? `${label}.${DEV_PREVIEW_ROOT}` : null
}

/**
 * Where entering a project on `tenantHost` should land. On a preview login
 * host, the dev tenant's projects open in the matching preview desktop, so
 * the whole flow stays inside the preview env. That preview desktop only
 * serves the dev tenant, so every other tenant, and every host outside a
 * preview login, keeps its real host.
 */
export function getProjectEntryHost(
  tenantHost: string,
  currentHostname: string,
): string {
  const previewDesktopHost = getPreviewDesktopHost(currentHostname)

  return previewDesktopHost &&
    normalizeHostName(tenantHost) === DEV_PREVIEW_ROOT
    ? previewDesktopHost
    : tenantHost
}
